import { useState } from "react";
import { IconX } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { parseDatasheetSummary } from "../../../products/utils/datasheet-extraction-schema";

interface Props {
  aiSummary: string;
  extractedText?: string | null;
  provider?: string | null;
  model?: string | null;
  costUSD?: number | null;
  createdAt?: string | null;
  onClose: () => void;
}

type Tab = "summary" | "raw";

export function AdminDatasheetExtractionDialog({
  aiSummary,
  extractedText,
  provider,
  model,
  costUSD,
  createdAt,
  onClose,
}: Props) {
  const [tab, setTab] = useState<Tab>("summary");
  const parsed = parseDatasheetSummary(aiSummary);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[85vh] w-full max-w-3xl flex-col gap-4 rounded-2xl bg-bgWhite p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <Heading variant="h3" tone="dark">AI extracted data</Heading>
            <Text variant="bodySm" tone="muted">
              {[
                provider && model ? `${provider} · ${model}` : provider ?? model ?? null,
                costUSD != null ? `$${costUSD.toFixed(4)}` : null,
                createdAt ? new Date(createdAt).toLocaleString() : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Text>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex size-9 items-center justify-center rounded-full text-textBody hover:bg-bgLight"
          >
            <IconX size={20} />
          </button>
        </div>

        <SegmentedControl
          value={tab}
          onChange={(v) => setTab(v as Tab)}
          options={[
            { value: "summary", label: "Structured summary" },
            { value: "raw", label: "Raw text" },
          ]}
        />

        <div className="flex-1 overflow-auto rounded-xl border border-borderDefault bg-bgLight p-4">
          {tab === "summary" ? (
            parsed ? <StructuredSummary summary={parsed} /> : <NoData label="Could not parse the AI summary as JSON." />
          ) : extractedText ? (
            <pre className="whitespace-pre-wrap break-words font-mono text-[12px] leading-relaxed text-textBody">
              {extractedText}
            </pre>
          ) : (
            <NoData label="No raw text was captured for this extraction." />
          )}
        </div>
      </div>
    </div>
  );
}

function NoData({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center">
      <Text variant="body" tone="muted">{label}</Text>
    </div>
  );
}

function StructuredSummary({ summary }: { summary: ReturnType<typeof parseDatasheetSummary> }) {
  if (!summary) return null;
  return (
    <div className="flex flex-col gap-4">
      {summary.title ? <Row label="Title" value={summary.title} /> : null}
      {summary.manufacturer ? <Row label="Manufacturer" value={summary.manufacturer} /> : null}
      {summary.model_numbers?.length ? <Row label="Model numbers" value={summary.model_numbers.join(", ")} /> : null}
      {summary.summary ? <Row label="Summary" value={summary.summary} /> : null}
      {summary.key_features?.length ? <ListRow label="Key features" items={summary.key_features} /> : null}
      {summary.specifications && summary.specifications.length > 0 ? (
        <SpecsRow specs={summary.specifications} />
      ) : null}
      {summary.dimensions ? <Row label="Dimensions" value={summary.dimensions} /> : null}
      {summary.connectivity?.length ? <ListRow label="Connectivity" items={summary.connectivity} /> : null}
      {summary.power ? <Row label="Power" value={summary.power} /> : null}
      {summary.operating_conditions ? <Row label="Operating conditions" value={summary.operating_conditions} /> : null}
      {summary.certifications?.length ? <ListRow label="Certifications" items={summary.certifications} /> : null}
      {summary.in_the_box?.length ? <ListRow label="In the box" items={summary.in_the_box} /> : null}
      {summary.warranty ? <Row label="Warranty" value={summary.warranty} /> : null}
      {summary.revision ? <Row label="Revision" value={summary.revision} /> : null}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <Text variant="bodySm" tone="body">{label}</Text>
      <Text variant="body" tone="dark">{value}</Text>
    </div>
  );
}

function ListRow({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="flex flex-col gap-1">
      <Text variant="bodySm" tone="body">{label}</Text>
      <ul className="list-disc pl-5">
        {items.map((it, i) => (
          <li key={i}>
            <Text variant="body" tone="dark" inline>{it}</Text>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SpecsRow({ specs }: { specs: Array<{ name: string; value: string }> }) {
  return (
    <div className="flex flex-col gap-1">
      <Text variant="bodySm" tone="body">Specifications</Text>
      <div className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
        {specs.map((spec, i) => (
          <div key={`${spec.name}-${i}`} className="contents">
            <Text variant="bodySm" tone="muted">{spec.name}</Text>
            <Text variant="bodySm" tone="dark">{spec.value}</Text>
          </div>
        ))}
      </div>
    </div>
  );
}
